import { useFormik } from "formik";
import PropTypes from "prop-types";
import React from "react";
import { withTranslation } from "react-i18next";
import { useHistory, useLocation, withRouter } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Col, Container, Form, FormFeedback, Input, Row } from "reactstrap";
import { Organisation } from "src/sdk/models/Organisation";
import { OrganisationService } from "src/services/OrganisationService";
import * as Yup from "yup";

const service = new OrganisationService();

export const EditOrganisation = () => {
    const location = useLocation<{ state: Organisation }>();
    const history = useHistory();

    const organisationData = location.state as Organisation;

    const validation = useFormik({
        enableReinitialize: true,

        initialValues: {
            id: organisationData?.id,
            organisationUniqueKey: organisationData?.organisationUniqueKey || "",
            name: organisationData?.name || "",
            phone: organisationData?.phone || "",
            address: organisationData?.address || "",
            city: organisationData?.city || "",
        },

        validationSchema: Yup.object({
            name: Yup.string().required("Please enter organization name"),
            phone: Yup.string()
                .nullable()
                .matches(/^[0-9+\s-]*$/, "Phone number can contain only numbers and + - characters")
                .max(20, "Too long"),
            address: Yup.string().nullable().max(200, "Address too long"),
            city: Yup.string().nullable().max(100, "City name too long"),
        }),

        onSubmit: (values: Organisation) => {
            service
                .editOrganisation(values)
                .then((response) => {
                    toast.success("Organization successfully updated!");
                    history.push("/organisations");
                })
                .catch((error) => {
                    console.log("API ERROR");
                    console.log(error);
                    toast.error("Error updating organization.");
                });
        },
    });

    return (
        <React.Fragment>
            <div className="container-fluid">
                {/* Breadcrumb */}
                <div className="row">
                    <div className="col-12">
                        <div
                            style={{
                                paddingTop: 10,
                                paddingBottom: 20,
                                display: "flex",
                                flexDirection: "row",
                            }}
                        >
                            <Link style={{ color: "black" }} to="/organisations" className="nav_link">
                                <h5>Organizations</h5>
                            </Link>
                            <i className="bi bi-chevron-right"></i>
                            <h5 style={{ marginLeft: 13, opacity: 0.6 }}>Edit organization</h5>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div>
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            validation.handleSubmit();
                            return false;
                        }}
                    >
                        <Container fluid className="p-5">
                            <Row className="mb-3">
                                <Col lg={3}>
                                    <h5>Name:</h5>
                                </Col>
                                <Col lg={5}>
                                    <Input
                                        id="name"
                                        name="name"
                                        placeholder="Enter organization name"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.name}
                                        invalid={validation.touched.name && !!validation.errors.name}
                                    />
                                    {validation.touched.name && validation.errors.name && (
                                        <FormFeedback>{validation.errors.name}</FormFeedback>
                                    )}
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col lg={3}>
                                    <h5>Phone:</h5>
                                </Col>
                                <Col lg={5}>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        placeholder="Enter phone number"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.phone ?? ""}
                                        invalid={validation.touched.phone && !!validation.errors.phone}
                                    />
                                    {validation.touched.phone && validation.errors.phone && (
                                        <FormFeedback>{validation.errors.phone}</FormFeedback>
                                    )}
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col lg={3}>
                                    <h5>Address:</h5>
                                </Col>
                                <Col lg={5}>
                                    <Input
                                        id="address"
                                        name="address"
                                        placeholder="Enter address"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.address ?? ""}
                                        invalid={validation.touched.address && !!validation.errors.address}
                                    />
                                    {validation.touched.address && validation.errors.address && (
                                        <FormFeedback>{validation.errors.address}</FormFeedback>
                                    )}
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col lg={3}>
                                    <h5>City:</h5>
                                </Col>
                                <Col lg={5}>
                                    <Input
                                        id="city"
                                        name="city"
                                        placeholder="Enter city"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.city ?? ""}
                                        invalid={validation.touched.city && !!validation.errors.city}
                                    />
                                    {validation.touched.city && validation.errors.city && (
                                        <FormFeedback>{validation.errors.city}</FormFeedback>
                                    )}
                                </Col>
                            </Row>
                        </Container>

                        <Container fluid>
                            <div className="d-flex flex-row-reverse">
                                <div className="p-1">
                                    <button
                                        className="btn btn-primary btn-block"
                                        style={{ border: 0, backgroundColor: "#95CBB2", color: "#FFFFFF" }}
                                        type="submit"
                                    >
                                        Save
                                    </button>
                                </div>
                                <div className="p-1">
                                    <Link className="btn btn-secondary btn-block" to="/organisations">
                                        Cancel
                                    </Link>
                                </div>
                            </div>
                        </Container>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};

EditOrganisation.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
};

export default withTranslation()(withRouter(EditOrganisation));
